import { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { Controller, useForm } from 'react-hook-form';

import { Modal } from '@/components/ui/therapist/Modal';
import { Button } from '@/components/ui/therapist/Button';
import {
  DifficultyLevel,
  Template,
  TemplateMode,
} from '@/components/Template/types';
import { Input } from '@/components/ui/therapist/Input';
import { Text } from '@/components/ui/therapist/Text';
import RadioButton from '@/components/ui/therapist/RadioButton';

import { styles } from './styles';
import colors from '@/config/colors';
import { handleTemplateCreationUtil } from '@/utils/createTemplate';
import { useDebounce } from '@/hooks/useDebounce';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { SetItem } from '@/stores/store.types';
import { useNavigation } from '@react-navigation/native';
import { handleTemplateUpdateUtil } from '@/utils/updateTemplate';
import { fetchTemplates } from '@/api/services/templates';
import { logError } from '@/utils/logHandling';

interface SaveTemplateModalProps {
  isVisible: boolean;
  setIsVisible: (visible: boolean) => void;
  newSetItems?: SetItem[];
  onCancel: () => void;
  mode: TemplateMode;
  setType: 'Word' | 'Sentence';
  initFormData?: {
    name: string;
    description: string;
    difficulty: DifficultyLevel;
  };
  templateId?: string;
}

type SaveTemplateFormData = {
  name: string;
  description: string;
  difficulty: DifficultyLevel;
};

export const SaveTemplateModal = ({
  mode,
  isVisible,
  setIsVisible,
  newSetItems = [],
  templateId,
  onCancel,
  initFormData,
  setType,
}: SaveTemplateModalProps) => {
  const isDuplicatingMode = mode === 'create' && initFormData;
  const initializedKey = useRef<null | string>(null);
  const { showNotification } = useNotification();
  const { goBack } = useNavigation();
  const [templates, setTemplates] = useState<Template[]>([]);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const { templates: fetchedTemplates } = await fetchTemplates({
          setType,
        });
        setTemplates(fetchedTemplates || []);
      } catch (error) {
        setTemplates([]);
      }
    };

    loadTemplates();
  }, [setType]);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isValid },
  } = useForm<SaveTemplateFormData>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      difficulty: undefined,
    },
  });

  const { cancel, debouncedFunc: debouncedCheckTemplateName } = useDebounce(
    async (name: string) => {
      if (!name) {
        return true;
      }

      try {
        const isDuplicate =
          templates?.filter(
            (template) =>
              template.name.trim().toLowerCase() === name.trim().toLowerCase(),
          ).length > 0;

        if (isDuplicate) {
          setError('name', {
            type: 'validate',
            message: 'Dieser Vorlagenname ist bereits vergeben.',
          });
          return 'Dieser Vorlagenname ist bereits vergeben.';
        }
      } catch (error) {
        logError('Error validating template name', error);
        return 'Fehler bei der Überprüfung des Vorlagennamens.';
      }
    },
    500,
  );

  const validateName = async (name: string) => {
    if (mode === 'update') {
      return !!name || 'Bitte geben Sie den Namen an';
    }

    return await debouncedCheckTemplateName(name);
  };

  const handleTemplateCreation = async (
    name: string,
    description: string,
    difficulty: DifficultyLevel,
  ) => {
    await handleTemplateCreationUtil({
      name,
      description,
      difficulty,
      items: newSetItems,
      setType,
      goBack,
      showNotification,
      setIsVisible,
    });
  };

  const handleTemplateUpdate = async (
    name: string,
    description: string,
    difficulty: DifficultyLevel,
    templateId: string,
  ) => {
    await handleTemplateUpdateUtil({
      name,
      description,
      difficulty,
      templateId,
      items: newSetItems,
      goBack,
      showNotification,
      setIsVisible,
    });
  };

  const onSubmit = async (data: SaveTemplateFormData) => {
    // For duplicate and create new template
    if (!templateId || mode === 'create') {
      await handleTemplateCreation(
        data.name,
        data.description,
        data.difficulty,
      );
      return;
    }
    // For updating existing template
    handleTemplateUpdate(
      data.name,
      data.description,
      data.difficulty,
      templateId,
    );
  };

  useEffect(() => {
    if (!initFormData) {
      return;
    }

    const uniqueKey = JSON.stringify(initFormData);

    if (initializedKey.current === uniqueKey) {
      return;
    }

    const name =
      mode === 'create' ? initFormData.name + ' (Kopie)' : initFormData.name;

    reset({
      name,
      description: initFormData.description,
      difficulty: initFormData.difficulty,
    });

    initializedKey.current = uniqueKey;
  }, [initFormData, mode, reset]);

  useEffect(() => {
    return () => cancel();
  }, [cancel]);

  return (
    <Modal
      isContentScrollable
      title={mode === 'update' ? 'Vorlage bearbeiten' : 'Als Vorlage speichern'}
      onClose={() => setIsVisible(false)}
      isVisible={isVisible}
      confirmActionButton={
        <Button
          disabled={!isValid}
          label="Vorlage abspeichern"
          onPress={handleSubmit(onSubmit)}
        />
      }
      cancelActionButton={
        <Button
          look="secondary"
          label="Speichern abbrechen"
          onPress={onCancel}
        />
      }
    >
      <View>
        <Text style={styles.label}>
          Schwierigkeit
          {<Text style={{ color: colors.newUI.therapist.red70 }}> *</Text>}
        </Text>
        <Text style={{ fontSize: 14 }}>
          Geben Sie an, wie anspruchsvoll die Vorlage ist.
        </Text>
        <Controller
          control={control}
          name="difficulty"
          rules={{
            validate: (value) =>
              !!value || 'Bitte geben Sie den Schwierigkeiten an',
          }}
          render={({ field }) => (
            <View style={styles.difficulty}>
              <RadioButton
                label="Leicht"
                isSelected={field.value === 'easy'}
                onPress={() => field.onChange('easy')}
              />
              <RadioButton
                label="Mittel"
                isSelected={field.value === 'medium'}
                onPress={() => field.onChange('medium')}
              />
              <RadioButton
                label="Schwer"
                isSelected={field.value === 'hard'}
                onPress={() => field.onChange('hard')}
              />
            </View>
          )}
        />

        <Controller
          control={control}
          name="name"
          rules={{
            required: 'Bitte geben Sie den Namen an',
            validate: (value) => validateName(value),
          }}
          render={({ field }) => (
            <Input
              required
              label="Vorlage Name"
              additionalInfo={
                isDuplicatingMode
                  ? 'Passen Sie den Namen der Vorlage an.'
                  : 'Ein eindeutiger Name, der Ihnen hilft, die Vorlage schnell und problemlos wiederzuerkennen.'
              }
              value={field.value ?? ''}
              onChange={field.onChange}
              status={
                errors.name ? 'warning' : field.value ? 'validated' : undefined
              }
              statusMessage={errors.name?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          rules={{
            required: 'Bitte geben Sie die Beschreibung an',
          }}
          render={({ field }) => (
            <Input
              required
              label="Vorlage Beschreibung"
              additionalInfo={
                isDuplicatingMode
                  ? 'Passen Sie die Beschreibung der Vorlage an.'
                  : 'Beschreiben Sie die Vorlage, um sie eindeutig erkennen zu können.'
              }
              value={field.value ?? ''}
              onChange={field.onChange}
              status={
                errors.description
                  ? 'warning'
                  : field.value
                    ? 'validated'
                    : undefined
              }
              statusMessage={errors.description?.message}
            />
          )}
        />
      </View>
    </Modal>
  );
};
